import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  GitBranch, Users, Loader2, ChevronDown, ChevronUp, 
  User, Heart, MessageCircle,
  Crown, UserCircle, Baby, Users2
} from "lucide-react";

// Relationship icons and colors mapping
const relationshipConfig = {
  "Self": { icon: Crown, color: "bg-amber-500", textColor: "text-amber-500" },
  "Father": { icon: User, color: "bg-blue-600", textColor: "text-blue-600" },
  "Mother": { icon: User, color: "bg-pink-500", textColor: "text-pink-500" },
  "Grandfather": { icon: User, color: "bg-blue-800", textColor: "text-blue-800" },
  "Grandmother": { icon: User, color: "bg-pink-700", textColor: "text-pink-700" },
  "Husband": { icon: Heart, color: "bg-red-500", textColor: "text-red-500" },
  "Wife": { icon: Heart, color: "bg-red-400", textColor: "text-red-400" },
  "Son": { icon: Baby, color: "bg-sky-500", textColor: "text-sky-500" },
  "Daughter": { icon: Baby, color: "bg-rose-400", textColor: "text-rose-400" },
  "Son-in-law": { icon: UserCircle, color: "bg-sky-600", textColor: "text-sky-600" },
  "Daughter-in-law": { icon: UserCircle, color: "bg-rose-500", textColor: "text-rose-500" },
  "Grandson": { icon: Baby, color: "bg-cyan-500", textColor: "text-cyan-500" },
  "Granddaughter": { icon: Baby, color: "bg-fuchsia-400", textColor: "text-fuchsia-400" },
  "Brother": { icon: Users2, color: "bg-indigo-500", textColor: "text-indigo-500" },
  "Sister": { icon: Users2, color: "bg-purple-400", textColor: "text-purple-400" },
  "Uncle": { icon: User, color: "bg-teal-600", textColor: "text-teal-600" },
  "Aunt": { icon: User, color: "bg-orange-400", textColor: "text-orange-400" },
  "Nephew": { icon: UserCircle, color: "bg-emerald-500", textColor: "text-emerald-500" },
  "Niece": { icon: UserCircle, color: "bg-lime-500", textColor: "text-lime-500" },
  "Cousin": { icon: Users2, color: "bg-violet-500", textColor: "text-violet-500" },
};

// Family Member Card Component
const FamilyMemberCard = ({ member, isCurrentUser, onViewProfile, onMessage }) => {
  const config = relationshipConfig[member.relationship] || { 
    icon: User, 
    color: "bg-gray-500", 
    textColor: "text-gray-500" 
  };
  const IconComponent = config.icon;

  return (
    <div 
      className={`relative flex flex-col items-center p-3 rounded-xl transition-all duration-300 hover:scale-105 ${
        isCurrentUser 
          ? 'bg-gradient-to-br from-primary/20 to-primary/5 ring-2 ring-primary shadow-lg' 
          : 'bg-card hover:bg-accent/50 shadow-md hover:shadow-lg'
      }`}
    >
      {/* Relationship Badge */}
      <div className={`absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${config.color}`}>
        {member.relationship}
      </div>
      
      {/* Avatar */}
      <div className="relative mt-2">
        <Avatar className="w-16 h-16 border-2 border-background shadow-md">
          <AvatarImage src={member.avatar} alt={member.name} />
          <AvatarFallback className={`${config.color} text-white font-bold`}>
            {member.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??'}
          </AvatarFallback>
        </Avatar>
        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full ${config.color} flex items-center justify-center shadow-md`}>
          <IconComponent className="w-3 h-3 text-white" />
        </div>
        {isCurrentUser && (
          <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shadow-md">
            <Crown className="w-3 h-3 text-white" />
          </div>
        )}
      </div>

      {/* Name */}
      <p className="mt-2 text-sm font-semibold text-center line-clamp-2 max-w-[100px]">
        {member.name}
      </p>
      
      {/* Member Code */}
      <p className="text-[10px] text-muted-foreground font-mono">
        {member.lf_id}
      </p>

      {/* Action Buttons */}
      {!isCurrentUser && member.user_id && (
        <div className="flex gap-1 mt-2">
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 w-7 p-0"
            onClick={() => onViewProfile(member.user_id)}
          >
            <User className="w-3 h-3" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 w-7 p-0"
            onClick={() => onMessage(member.user_id)}
          >
            <MessageCircle className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

// Tree Branch/Connector Component
const TreeConnector = ({ type = "vertical" }) => {
  if (type === "vertical") {
    return (
      <div className="flex justify-center">
        <div className="w-0.5 h-8 bg-gradient-to-b from-primary/60 to-primary/30 rounded-full" />
      </div>
    );
  }
  
  if (type === "horizontal") {
    return (
      <div className="h-0.5 flex-1 bg-gradient-to-r from-primary/30 via-primary/60 to-primary/30 rounded-full mx-2" />
    );
  }
  
  return null;
};

// Family Tree Visual Component
const FamilyTreeVisual = ({ familyTree, currentUserLfId, onViewProfile, onMessage }) => {
  // Group members by generation/relationship type
  const groupMembers = useCallback(() => {
    const membersList = familyTree.members || [];
    const groups = {
      grandparents: [],
      parents: [],
      self_spouse: [],
      siblings: [],
      children: [],
      grandchildren: [],
      extended: [],
    };
    
    membersList.forEach(member => {
      const rel = member.relationship;
      if (["Grandfather", "Grandmother"].includes(rel)) {
        groups.grandparents.push(member);
      } else if (["Father", "Mother", "Uncle", "Aunt"].includes(rel)) {
        groups.parents.push(member);
      } else if (["Self", "Husband", "Wife"].includes(rel)) {
        groups.self_spouse.push(member);
      } else if (["Brother", "Sister", "Cousin"].includes(rel)) {
        groups.siblings.push(member);
      } else if (["Son", "Daughter", "Son-in-law", "Daughter-in-law"].includes(rel)) {
        groups.children.push(member);
      } else if (["Grandson", "Granddaughter"].includes(rel)) {
        groups.grandchildren.push(member);
      } else {
        groups.extended.push(member);
      }
    });
    
    return groups;
  }, [familyTree.members]);
  
  const groups = groupMembers();
  
  const renderGenerationRow = (rowMembers, label, showConnectorAbove = false, showConnectorBelow = false) => {
    if (rowMembers.length === 0) return null;
    
    return (
      <div className="flex flex-col items-center">
        {showConnectorAbove && <TreeConnector type="vertical" />}
        
        {/* Generation Label */}
        <div className="flex items-center gap-2 mb-3">
          <div className="h-px flex-1 bg-border w-8" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {label}
          </span>
          <div className="h-px flex-1 bg-border w-8" />
        </div>
        
        {/* Members Row */}
        <div className="flex flex-wrap justify-center gap-4">
          {rowMembers.map((member, idx) => (
            <FamilyMemberCard
              key={member.user_id || idx}
              member={member}
              isCurrentUser={member.lf_id === currentUserLfId}
              onViewProfile={onViewProfile}
              onMessage={onMessage}
            />
          ))}
        </div>
        
        {showConnectorBelow && <TreeConnector type="vertical" />}
      </div>
    );
  };

  return (
    <div className="py-6 space-y-2 overflow-x-auto">
      {/* Grandparents */}
      {renderGenerationRow(groups.grandparents, "Grandparents", false, groups.parents.length > 0)}
      
      {/* Parents & Extended */}
      {renderGenerationRow(
        [...groups.parents, ...groups.extended], 
        "Parents & Elders", 
        groups.grandparents.length > 0, 
        groups.self_spouse.length > 0 || groups.siblings.length > 0
      )}
      
      {/* Self, Spouse & Siblings */}
      {renderGenerationRow(
        [...groups.self_spouse, ...groups.siblings], 
        "You & Siblings", 
        groups.parents.length > 0 || groups.extended.length > 0, 
        groups.children.length > 0
      )}
      
      {/* Children */}
      {renderGenerationRow(groups.children, "Children", groups.self_spouse.length > 0, groups.grandchildren.length > 0)}
      
      {/* Grandchildren */}
      {renderGenerationRow(groups.grandchildren, "Grandchildren", groups.children.length > 0, false)}
    </div>
  );
};

// Main Page Component
const FamilyTreePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [familyTrees, setFamilyTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTree, setExpandedTree] = useState(null);

  useEffect(() => {
    fetchMyFamilyTrees();
  }, []);

  const fetchMyFamilyTrees = async () => {
    try {
      const response = await api.get("/family-trees/my-family");
      setFamilyTrees(response.data);
      // Auto-expand first tree if only one exists
      if (response.data.length === 1) {
        setExpandedTree(response.data[0].id);
      }
    } catch (_error) {
      toast.error("Failed to load family trees");
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfile = (userId) => {
    navigate(`/user/${userId}`);
  };

  const handleMessage = async (userId) => {
    try {
      const response = await api.post("/conversations", { participant_id: userId });
      navigate(`/messages/${response.data.id}`);
    } catch (_error) {
      toast.error("Failed to start conversation");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-6 px-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl bg-primary/10">
            <GitBranch className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">My Family Tree</h1>
            <p className="text-muted-foreground text-sm">
              View your family connections and relationships
            </p>
          </div>
        </div>

        {/* No Family Trees */}
        {familyTrees.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                <Users className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Family Tree Yet</h3>
              <p className="text-muted-foreground text-center max-w-md mb-4">
                You are not part of any verified family tree yet. Family trees are created and verified by the admin.
              </p>
              <Badge variant="secondary" className="text-sm">
                Contact Admin to Create Your Family Tree
              </Badge>
            </CardContent>
          </Card>
        ) : (
          /* Family Trees List */
          <div className="space-y-4">
            {familyTrees.map((tree) => (
              <Card key={tree.id} className="overflow-hidden">
                {/* Tree Header */}
                <CardHeader 
                  className="cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => setExpandedTree(expandedTree === tree.id ? null : tree.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <GitBranch className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {tree.family_name}
                          <Badge variant="outline" className="font-mono text-xs">
                            {tree.family_id}
                          </Badge>
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {tree.members?.length || 0} family members
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                        Verified
                      </Badge>
                      {expandedTree === tree.id ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {/* Expanded Tree View */}
                {expandedTree === tree.id && (
                  <CardContent className="border-t bg-gradient-to-b from-muted/30 to-background">
                    <FamilyTreeVisual
                      familyTree={tree}
                      currentUserLfId={user?.member_code}
                      onViewProfile={handleViewProfile}
                      onMessage={handleMessage}
                    />
                    
                    {/* Legend */}
                    <div className="mt-6 pt-4 border-t">
                      <p className="text-xs text-muted-foreground mb-3 font-medium">RELATIONSHIP LEGEND</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(relationshipConfig).slice(0, 10).map(([rel, config]) => (
                          <div 
                            key={rel} 
                            className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50 text-xs"
                          >
                            <div className={`w-3 h-3 rounded-full ${config.color}`} />
                            <span>{rel}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50 h-fit">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="space-y-1">
                <p className="font-medium text-blue-900 dark:text-blue-100">
                  About Family Trees
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Family trees are created by administrators and must be verified before they appear here. 
                  Each verified family receives a unique Family ID (e.g., FAM-00001). 
                  Contact the admin if you want to create or update your family tree.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default FamilyTreePage;
